import API from "../../services/api";
import { getEmployees } from "../Employees/EmployeeApi";

/** Alias used by Allowances & Benefits pages */
export const listEmployees = getEmployees;

/* ── Fuel Reimbursement ─────────────────────────────────────── */

export async function getFuelSettings() {
  const { data } = await API.get("/fuel-reimbursement/settings");
  return data;
}

export async function putFuelSettings(enabled) {
  const { data } = await API.put("/fuel-reimbursement/settings", { enabled: Boolean(enabled) });
  return data;
}

export async function getFuelOverview() {
  const { data } = await API.get("/fuel-reimbursement/overview");
  return data;
}

export async function getFuelAllocations({ eligibleOnly = true } = {}) {
  const { data } = await API.get("/fuel-reimbursement/allocations", {
    params: { eligible_only: eligibleOnly },
  });
  return data;
}

export async function putFuelEligibility(employeeId, { eligible, monthly_limit }) {
  const { data } = await API.put(`/fuel-reimbursement/employees/${employeeId}/eligibility`, {
    eligible: Boolean(eligible),
    monthly_limit: monthly_limit === "" || monthly_limit == null ? null : Number(monthly_limit),
  });
  return data;
}

export async function getFuelBills(params = {}) {
  const { data } = await API.get("/fuel-reimbursement/bills", { params });
  return data;
}

export async function getEmployeeFuelBills(employeeId, params = {}) {
  const { data } = await API.get(`/fuel-reimbursement/employees/${employeeId}/bills`, { params });
  return data;
}

export async function createFuelBill(employeeId, formData) {
  const { data } = await API.post(`/fuel-reimbursement/employees/${employeeId}/bills`, formData);
  return data;
}

/** Extract duplicate payload from a 409 FuelReimbursementError response. */
export function duplicateFromError(err) {
  const detail = err?.response?.data?.detail;
  if (err?.response?.status !== 409) return null;
  if (detail && typeof detail === "object" && detail.code === "DUPLICATE_FUEL_BILL" && detail.existing) {
    return detail.existing;
  }
  return null;
}

export function apiMessage(err, fallback = "Something went wrong.") {
  if (err?.response?.status === 413) {
    return "The file is too large. Use a PDF, JPG, or PNG under 10 MB.";
  }
  const detail = err?.response?.data?.detail ?? err?.response?.data;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string" && detail.message.trim()) {
    return detail.message;
  }
  if (Array.isArray(detail)) {
    const text = detail.map((item) => item?.msg || item).filter(Boolean).join(" ");
    if (text) return text;
  }
  return fallback;
}

export async function updateFuelBill(billId, body) {
  const { data } = await API.patch(`/fuel-reimbursement/bills/${billId}`, body);
  return data;
}

export async function deleteFuelBill(billId) {
  const { data } = await API.delete(`/fuel-reimbursement/bills/${billId}`);
  return data;
}

export async function approveFuelBill(billId, { allowDuplicate = false } = {}) {
  const { data } = await API.post(`/fuel-reimbursement/bills/${billId}/approve`, null, {
    params: allowDuplicate ? { allow_duplicate: true } : undefined,
  });
  return data;
}

export async function rejectFuelBill(billId, rejection_reason) {
  const { data } = await API.post(`/fuel-reimbursement/bills/${billId}/reject`, {
    rejection_reason,
  });
  return data;
}

/* ── Advance Taken ──────────────────────────────────────────── */

const ADV = "/salary/v2/enterprise";

export async function getAdvanceOverview() {
  const { data } = await API.get(`${ADV}/advances/overview`);
  return data;
}

export async function getAdvanceAllocations({ enabledOnly = true } = {}) {
  const { data } = await API.get(`${ADV}/advances/allocations`, {
    params: { enabled_only: enabledOnly },
  });
  return data;
}

export async function getOrgAdvances(params = {}) {
  const { data } = await API.get(`${ADV}/advances/org`, { params });
  return data;
}

export async function putAdvanceSettings(enabled) {
  const { data } = await API.put(`${ADV}/advances/settings`, { enabled: Boolean(enabled) });
  return data;
}

export async function putAdvanceEligibility(employeeId, { eligible }) {
  const { data } = await API.put(`${ADV}/advances/employees/${employeeId}/eligibility`, {
    eligible: Boolean(eligible),
  });
  return data;
}

export async function getEmployeeAdvances(employeeId) {
  const { data } = await API.get(`${ADV}/advances`, { params: { employee_id: employeeId } });
  return data;
}

export async function createAdvance(body) {
  const { data } = await API.post(`${ADV}/advances`, body);
  return data;
}

export async function updateAdvance(advanceId, body) {
  const { data } = await API.patch(`${ADV}/advances/${advanceId}`, body);
  return data;
}

export async function approveAdvance(advanceId, { allowDuplicate = false } = {}) {
  const { data } = await API.post(`${ADV}/advances/${advanceId}/approve`, null, {
    params: allowDuplicate ? { allow_duplicate: true } : undefined,
  });
  return data;
}

export async function rejectAdvance(advanceId, rejection_reason) {
  const { data } = await API.post(`${ADV}/advances/${advanceId}/reject`, { rejection_reason });
  return data;
}

export async function cancelAdvance(advanceId) {
  const { data } = await API.post(`${ADV}/advances/${advanceId}/cancel`);
  return data;
}

/** Extract duplicate advance payload from a 409 response. */
export function advanceDuplicateFromError(err) {
  const detail = err?.response?.data?.detail;
  if (err?.response?.status !== 409) return null;
  if (detail && typeof detail === "object" && detail.code === "DUPLICATE_ADVANCE" && detail.existing) {
    return detail;
  }
  return null;
}
